/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2022, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injector, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import * as _ from 'underscore';
import { DiService } from '@app/di/di.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { DiConfigService } from '@app/di/di-config.service';
import { SignOutService } from '@app/common/sign-out.service';
import { NotifyService } from '@app/common/notify.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { ExternalAppContextService } from '@app/di/external-app-context.service';
import { AppConfigService } from '@app/common/app-config.service';
import { UtilService } from '@app/util/util.service';
import { Logger, MessageService } from '@core/lib/services';
import { TranslateService } from '@ngx-translate/core';
import { isUndefined } from '@core/lib/helpers/helpers';

const log = new Logger('LineShaftHandlerService');
declare const DesktopLauncher: any;

export interface LineShaftIntegration {
  subscribePluginResponse(retObj?: any);
  loadPlugin();
  cleanup(): boolean;
  /* initialize();
  reset();*/
}

@Injectable({
  providedIn: 'root'
})
export class LineShaftHandlerService {
  private UVIEWER_CONFIG;
  private LoadMessageQueueParams;
  private uvUpdater = null;
  private xmlTemplateManagerService;
  installSecondPopupDisplayed: boolean;

  // FILO queue for cleanup callbacks
  private readonly cleanupCallbacks: (() => void)[] = [];

  isLineShaftInitialized = false;

  uvserver;
  uvPort;
  uvModuleName;
  codebase;
  sharedIntegrationContext;
  ownerInstance;
  installPopupDisplayed: boolean;

  // Observers
  private readonly _loadLineShaftPlugin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this.isLineShaftInitialized
  );
  private readonly _lineShaftResponseMessage: Subject<any> = new Subject();
  private readonly _lineShaftLifecycleMessage: Subject<any> = new Subject();

  public readonly channelObservers = {
    onReady: this._loadLineShaftPlugin.asObservable(),
    onResponseMessage: this._lineShaftResponseMessage.asObservable(),
    onLifecycleChange: this._lineShaftLifecycleMessage.asObservable()
  };

  constructor(
    private injector: Injector,
    private messageService: MessageService,
    private translate: TranslateService,
    private utilService: UtilService,
    private diConfigService: DiConfigService,
    private signOutService: SignOutService,
    private notify: NotifyService,
    private integrationStatusService: IntegrationStatusService,
    private diService: DiService,
    private externalAppContext: ExternalAppContextService,
    private appConfig: AppConfigService
  ) {
    log.info('LineShaft constructor invoked');
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;
    this.uvserver = this.UVIEWER_CONFIG.SERVER;
    this.uvPort = this.UVIEWER_CONFIG.PORT;
    this.uvModuleName = this.UVIEWER_CONFIG.MODULENAME;
    this.codebase = this.UVIEWER_CONFIG.CODEBASE;
    this.ownerInstance = this.UVIEWER_CONFIG.OWNERINSTANCE;

    this.LoadMessageQueueParams = this.diService.LoadMessageQueueParams;

    this.sharedIntegrationContext = {
      tabs: this.utilService.sharedTabContext,
      iSiteViewerLaunch: undefined,
      uViewOpened: undefined
    };

    this.xmlTemplateManagerService = this.injector.get(XmlTemplateManagerService);
    this.diService.initialize();

    // Check query parameter first, if not present then check configuration
    const queryParamValue = this.utilService.getQueryParam('enableLineShaft');
    const configValue = this.appConfig.getAppConfigByKey('enableSiteLineShaft');

    console.log(
      '[LineShaft] enableLineShaft queryParamValue (from URL):',
      queryParamValue,
      '| type:',
      typeof queryParamValue
    );
    console.log('[LineShaft] enableSiteLineShaft (from config):', configValue);

    const enableLineShaft =
      queryParamValue !== null && queryParamValue !== undefined ? queryParamValue === true : configValue === true;

    console.log(
      '[LineShaft] Final decision — enableLineShaft:',
      enableLineShaft,
      '| driven by:',
      queryParamValue !== null && queryParamValue !== undefined ? 'queryParam' : 'config'
    );

    if (enableLineShaft) {
      this.uvUpdater = new DesktopLauncher(true);
      this.initializeLineShaftWebSocket();
    } else {
      log.info(
        "LineShaft initialization is disabled. To enable, set 'enableSiteLineShaft=true' in configuration or use URL parameter 'enableLineShaft=true'."
      );
    }
  }


  onResponseHandler = responseXML => {
    try {
      const responseInfo = this.diService.parseResponseXML(responseXML);
      log.info('onResponseHandler - Response Info :  ; \n\r ' + JSON.stringify(responseInfo));

      // Event to inform message from lineshaft to registered integration
      log.debug('lineShaft ResponseMessage sent', { responseInfo, responseXML });
      this.messageService.broadcast('handleLineShaftMessage', { responseInfo, responseXML });
      this._lineShaftResponseMessage.next({ responseInfo, responseXML });

      this.handleLineShaftWebSocketResponse(responseInfo);
      this.LSPluginManagerResponseHandler(responseInfo);
      this.LsPluginSetcontextResponseHandler(responseInfo);
      this.triggerProcessQueueOnResponse(responseInfo);
      log.debug('onResponseHandler -LEAVE ; \n\r ' + responseInfo.toString());
    } catch (error) {
      log.error('onResponseHandler - unhandled exception, error : ' + error + ', Response XML - ' + responseXML);
    }
  };

  private handleLineShaftWebSocketResponse(responseInfo) {
    if (responseInfo.sourceName === 'LINESHAFT_WEBSOCKET_SERVER') {
      if (responseInfo.topicName === 'ANOTHER_INSTANCE_INITIATED') {
        log.error('handleLineShaftWebSocketResponse - Automatic sign out on multiple instance error');
        this.signOutService.automaticSignOut();
      } else if (responseInfo.topicName === 'SET_OWNERINSTANCE_RESPONSE') {
        if (responseInfo.resultValue === 'SUCCESS') {
          log.info('handleLineShaftWebSocketResponse - LoadPlugins initiated');
          if (!this.isLineShaftInitialized) {
            this.isLineShaftInitialized = true;
            this.loadPluginHandler();
          }
        } else {
          log.error('handleLineShaftWebSocketResponse - Client not connected to LineShaft');
          this.notify.critical(
            this.translate.instant('di.desktopIntegration.warning.integrationDisabled'),
            this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
          );
          this.disableDesktopIntegration();
        }
      }
    }
  }

  private processQueueOnLoadResponse(responseInfo) {
    for (let index = 0; index < this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.length; index++) {
      log.debug(
        'processQueueOnLoadResponse - messageWaitingForResponse.responseSource' +
          ' sourceName : ' +
          this.LoadMessageQueueParams.messageWaitingForResponse.responseSource[index] +
          'responseInfo sourceName : ' +
          responseInfo.sourceName
      );
      // If source name in response matches the source name in responseSource list then remove the source from
      // the list
      if (responseInfo.sourceName === this.LoadMessageQueueParams.messageWaitingForResponse.responseSource[index]) {
        // If source name is LineShaftPluginManager then check the module name to confirm the response.
        if (responseInfo.sourceName === this.UVIEWER_CONFIG.PLUGIN_MANAGER) {
          if (responseInfo.moduleName === this.LoadMessageQueueParams.messageWaitingForResponse.moduleName) {
            this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.splice(index, 1);
            log.debug(
              'processQueueOnLoadResponse - Load Response received from' +
                ' LineShaftPluginManager, response list count : ' +
                this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.length
            );
          }
        } else {
          this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.splice(index, 1);
          log.debug(
            'processQueueOnLoadResponse - Load Response received from plugin,' +
              ' response list count : ' +
              this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.length
          );
        }
        break;
      }
    }
    // If message response waiting list is empty process the next request from queue
    if (this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.length === 0) {
      log.info(
        'processQueueOnLoadResponse - Load response received from both plugin and' +
          ' LineShaftPluginManager process next request from queue'
      );
      this.LoadMessageQueueParams.messageWaitingForResponse = undefined;
      this.diService.clearTimeout();
      this.diService.processQueue();
    }
  }

  triggerProcessQueueOnResponse(responseInfo) {
    // messageWaitingForResponse = undefined means there is no message waiting for its response
    if (this.LoadMessageQueueParams.messageWaitingForResponse !== undefined) {
      log.info('triggerProcessQueueOnResponse - waiting for response is true');
      // If Load response is received
      if (responseInfo.topicName.indexOf('LOAD_RESPONSE') > 0) {
        // Desktop Integration need to process Load requests one by one that
        // means send one load request and wait
        // for its response then send next load request and so on.
        // Desktop integration expects load response from two source - LineShaftPluginManager and Plugin.
        // That means process next load request from queue must happen only after receiving response from two.
        //  sources. This is implemented in a way like, for each load request
        // 1. save the request along with source names from which Desktop Integration
        //    expecting response and module name in to LSMessage.
        // 2. Save that LSMessage in to this.LoadMessageQueueParams.messageWaitingForResponse
        // 3. For each response of this request check the source name, remove the source name from
        //    messageWaitingForResponse.responseSource
        // 4. When messageWaitingForResponse.responseSource list is empty, that means response received from,
        //    expected sources process the next load request from queue
        log.info('triggerProcessQueueOnResponse - load response received');
        this.processQueueOnLoadResponse(responseInfo);
      }
    }
  }

  private notifyUserAndSignOut() {
    // Notify user if logout not initiated already
    if (!this.diService.isLogoutInitiated()) {
      this.disableDesktopIntegration();
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.lineShaftFailed'),
        this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
      );
      log.error('Sign out RadDesktop after showing the message since LineShaft crashed.');
      // Sign out RadDesktop after showing the message since LineShaft crashed.
      const signoutTimeout = setTimeout(() => {
        this.signOutService.signOut();
        clearTimeout(signoutTimeout);
      }, 10000);
    }
  }

  // LineShaftPluginManager response handler
  LSPluginManagerResponseHandler(responseInfo) {
    this.checkApplicationTerminatedTopic(responseInfo);
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE' ||
      responseInfo.topicName === 'RadDesktop.CTX_CTRL_MESSAGE_RESPONSE'
    ) {
      if (responseInfo.sourceName === 'LineShaftPluginManager' && responseInfo.resultValue === 'FAILURE') {
        log.error('LSPluginManagerResponseHandler: Lineshaft crash, desktop integration disabled');
        this.notifyUserAndSignOut();
      }
    }
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager'
    ) {
      if (responseInfo.resultValue === 'FAILURE') {
        log.error('LSPluginManagerResponseHandler - Load plugin failed');
        // Plugin rename error scenario: In this case there is no response from corresponding plugin. So whenever
        // there is a load failure message from LSPluginManager, Desktop Integration need to clear timer and
        // process next request from queue. This is implemented by comparing module name in response and
        // messageWaitingForResponse. As per current implementation only load requests are having message waiting
        //  source name. In future if setcontext requests are also updated with the message waiting source name
        // this logic need to change.
        if (
          this.LoadMessageQueueParams.messageWaitingForResponse !== undefined &&
          this.LoadMessageQueueParams.messageWaitingForResponse.responseSource.length !== 0
        ) {
          if (
            this.LoadMessageQueueParams.messageWaitingForResponse.responseSource[0].indexOf(responseInfo.moduleName) >=
            0
          ) {
            log.info(
              'LSPluginManagerResponseHandler: load response from ' +
                responseInfo.moduleName +
                ' is pending, process next request from queue'
            );
            this.LoadMessageQueueParams.messageWaitingForResponse = undefined;
            this.diService.clearTimeout();
            this.diService.processQueue();
          }
        }
      }
    }
  }

  private checkApplicationTerminatedTopic(responseInfo) {
    if (
      responseInfo.topicName === 'TERMINATE_APPLICATION_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager'
    ) {
      log.info(
        'Received APPLICATION_TERMINATED Response For Application : ' +
          responseInfo.applicationName +
          ', Result Value : ' +
          responseInfo.resultValue
      );
      if (
        responseInfo.applicationName === this.appConfig.getAppConfigByKey('reportingAppProcessName') &&
        responseInfo.resultValue === 'SUCCESS'
      ) {
        // WFM will initiate terminate application in 2 different scenarios
        // 1. Reset integration - when PowerScribe is in not responding state, reset the integration.
        // 2. LineShaft restart - during LineShaft restart to prevent out of context issues, terminate the application.
        // In the first scenario only we need to follow the below logic. In the first scneario the integration status
        // is 'Disconnected' and in second scenario it is 'Plugin Not Loaded'. The Disconnected status is used to
        // identify the first scneario.
        if (!this.integrationStatusService.isReportActive()) {
          log.info('broadcast closePlugin');
          this.messageService.broadcast('closePlugin');
        }
      } else if (responseInfo.resultValue === 'FAILURE') {
        log.error('checkApplicationTerminatedTopic - Application termination failed : ' + responseInfo.applicationName);
      }
    }
  }

  /**
   * Viewer, and CCOW setContext response handler.
   */
  LsPluginSetcontextResponseHandler(responseInfo) {
    if (responseInfo.topicName === 'LS_WFMCONTEXT_REQUEST') {
      this.externalAppContext.handleGExInboundSetcontextResponse(responseInfo);
    }
    // Check if viewer failed to launch
    // Launch viewer failed.
    if (responseInfo.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE') {
      log.info('RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE received from source: ' + responseInfo.sourceName);
    }
  }

  /**
   * disableDesktopIntegration - Disable all the integrations
   */
  disableDesktopIntegration() {
    log.info('disableDesktopIntegration - disable all the integrations');
    this.integrationStatusService.disableDesktopIntegration();
  }

  /**
   * loadPluginHandler - Handle the plugin load
   */
  loadPluginHandler() {
    this.subscribeApplicationTerminateFromLineShaft();
    this.subscribePluginCloseResponse();

    // Event to inform integrations using lineshaft to load plugins
    log.info('LineShaft initialized and ready to load plugins');
    this._loadLineShaftPlugin.next(this.isLineShaftInitialized);
  }

  /**
   * Method to handle the uvUpdater response.
   * response - response object
   */
  handleUVUpdaterResponse(response) {
    if (response.Value === 'WebSocketConnected') {
      log.info('handleUVUpdaterResponse - UVUpdater is ready');
    } else if (response.Value === 'WebSocketError') {
      log.error('handleUVUpdaterResponse - UVUpdater WebSocket connection failed');
      this.notify.critical(
        this.translate.instant('uvUpdater.connection.failed'),
        this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
      );
      this.disableDesktopIntegration();
    } else if (response.Value === 'Terminated') {
      log.error('handleUVUpdaterResponse - UVUpdater Terminated, relaunching it to support integration');
      const modalInstance = this.notify.critical(
        this.translate.instant('uvUpdater.terminated'),
        this.translate.instant('di.desktopIntegration.title')
      );
      modalInstance.finally(() => {
        this.uvUpdater.reset();
        this.uvUpdater.connectUVUpdater();
      });
    } else if (response.Action === 'Install') {
      log.error('handleUVUpdaterResponse - UVUpdater not installed');
      if (this.installPopupDisplayed) {
        if (this.installSecondPopupDisplayed) {
          log.error('handleUVUpdaterResponse - UVUpdater install popup already displayed');
          this.notify.critical(
            this.translate.instant('uvUpdater.connection.failed'),
            this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
          );
          this.disableDesktopIntegration();
          return;
        } else {
          this.handleSecondMessage();
        }
      } else {
        const uvUpdaterNotifier1 = this.notify.decision(this.translate.instant('uvUpdater.install.msg1'), {
          disableHeaderCloseBtn: true,
          headerContent: this.translate.instant('uvUpdater.install.header'),
          disableNoBtn: true,
          yesButtonLabel: this.translate.instant('label.continue'),
          isDecisionModal: true,
          enableYesBtn: true
        });
        if (uvUpdaterNotifier1) {
          const keyUVUpdaterInstall = 'keyUVUpdaterInstallationStatus';
          // Set the flag to prevent the install popup display multiple times
          this.installPopupDisplayed = true;
          uvUpdaterNotifier1.then(data => {
            if (data === 'YES') {
              console.log('uvUpdaterNotifier1');
              this.uvUpdater.reset();
              this.uvUpdater.connectUVUpdater();
              this.displayNotificationMsgDetailsInSystemStatus(keyUVUpdaterInstall, '');
            } else {
              log.error('handleUVUpdaterResponse - UVUpdater installation cancelled');
              this.displayNotificationMsgDetailsInSystemStatus(
                keyUVUpdaterInstall,
                this.translate.instant('systemStatusMsg.uvUpdaterInstallCancel')
              );
              this.notify.critical(
                this.translate.instant('uvUpdater.connection.failed'),
                this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
              );
              this.disableDesktopIntegration();
            }
          });
        }
      }
    }
  }

  handleSecondMessage() {
    const uvUpdaterURI = 'https://' + this.uvserver + '/downloads/runtime/UVUpdater/UVUpdaterSetup.msi';
    const timestamp = new Date().getTime();
    const uvUpdaterNotifier2 = this.notify.decision(this.translate.instant('uvUpdater.install.msg2'), {
      disableHeaderCloseBtn: true,
      headerContent: this.translate.instant('uvUpdater.install.header'),
      noButtonLabel: this.translate.instant('label.cancel'),
      yesButtonLabel: this.translate.instant('label.done'),
      isDecisionModal: true,
      enableYesBtn: true,
      showUri: true,
      uri: uvUpdaterURI + '?timestamp=' + timestamp
    });
    if (uvUpdaterNotifier2) {
      this.installSecondPopupDisplayed = true;
      const keyUVUpdaterInstall = 'keyUVUpdaterInstallationStatus';
      // Set the flag to prevent the install popup display multiple times
      this.installPopupDisplayed = true;
      uvUpdaterNotifier2.then(data => {
        if (data === 'YES') {
          log.info('handleUVUpdaterResponse - Reconnecting UVUpdater');
          this.uvUpdater.reset();
          this.uvUpdater.connectUVUpdater();
          this.displayNotificationMsgDetailsInSystemStatus(keyUVUpdaterInstall, '');
        } else {
          log.error('handleUVUpdaterResponse - UVUpdater installation cancelled');
          this.displayNotificationMsgDetailsInSystemStatus(
            keyUVUpdaterInstall,
            this.translate.instant('systemStatusMsg.uvUpdaterInstallCancel')
          );
          this.notify.critical(
            this.translate.instant('uvUpdater.connection.failed'),
            this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
          );
          this.disableDesktopIntegration();
        }
      });
    }
  }

  /**
   * Method to handle the LineShaft response.
   * response - response object
   */
  handleLineShaftResponse(response) {
    if (response.Action === 'Launch' && response.Result === 'Error') {
      log.error('handleLineShaftResponse - LineShaft startup failed');
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.warning.integrationDisabled'),
        this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
      );
      this.disableDesktopIntegration();
    } else if (response.Value === 'Terminated') {
      log.error('handleLineShaftResponse - LineShaft Terminated, relaunching it to continue integration workflows');
      this.messageService.broadcast('resetPlugin');
      this.isLineShaftInitialized = false;
      const modalInstance = this.notify.critical(
        this.translate.instant('desktopIntegration.lineShaft.terminated'),
        this.translate.instant('desktopIntegration.stoppedWorking')
      );
      modalInstance.finally(() => {
        this.uvUpdater.launchLineShaft();
      });
    }
  }

  /**
   * Method to handle the uvUpdater response callback.
   * response - response object
   */
  uvUpdaterResponseCallBack = response => {
    log.debug('_lineShaftLifecycleMessage ' + response);
    this._lineShaftLifecycleMessage.next(response);
    if (response.Component === 'UVUpdater') {
      this.handleUVUpdaterResponse(response);
    } else if (response.Component === 'Lineshaft') {
      this.handleLineShaftResponse(response);
    }
  };

  /**
   * Initializes WebSocket connection and template XML's.
   */
  initializeLineShaftWebSocket() {
    // Setting the uvUpdater for invoke calls
    log.info('LineShaft initializing...');
    this.diService.setUVUpdater(this.uvUpdater);
    this.diService.setLoadMessageQueue(this.LoadMessageQueueParams);
    this.diService.setDIDisableCallback(this.disableDesktopIntegration);
    if (this.isCommonConfigInvalid()) {
      /* Common config settings are wrong */
      log.error('initializeLineShaftWebSocket- common configuration invalid, disabling the integration');
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.warning.integrationDisabled'),
        this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
      );
      this.integrationStatusService.setUViewerPluginStatus(true);
    } else {
      const initTimeout = setTimeout(() => {
        this.xmlTemplateManagerService.initialize().then(
          (data: any) => {
            log.info(
              'XMLTemplateManager initialize() resolved in initializeLineShaftWebSocket,' + ' XML Templates Received'
            );
            this.diService.errorCodeList = data.errorCodeList;
            if (this.diService.hasXMLConfError('DI')) {
              log.error(
                'XMLTemplateManager initialize() - Required template missing : ' + JSON.stringify(data.errorCodeList)
              );
              this.notify.critical(
                this.translate.instant('di.desktopIntegration.warning.integrationDisabled'),
                this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
              );
              this.integrationStatusService.setUViewerPluginStatus(true);
              this.integrationStatusService.setUViewerNavSyncPluginStatus(true);
              return;
            }

            /* Common configurations are valid, proceed with UVUpdater connection */
            this.uvUpdater.setSecurityLevel(2); // port=443
            this.uvUpdater.setWebSocketUsed(true);
            // do not append username to userSessionId, due to special characters unable to make a websocket connection
            this.uvUpdater.setUserSessionId('wfm' + UtilService.getUniqueIdentifier());
            this.uvUpdater.setUVServerIP(this.uvserver);
            this.uvUpdater.setUVServerPort(this.uvPort);
            this.uvUpdater.setOwnerInstance(this.ownerInstance);
            this.uvUpdater.setAppType(isUndefined(this.utilService.externalLoginApp) ? 'Self' : 'External');
            this.uvUpdater.setDataFormat('XML');
            const wfmPluginsVersion = this.diConfigService.getDiConfig().WFMPLUGINS_VERSION;
            this.uvUpdater.setPluginVersion(wfmPluginsVersion);
            const wfmContextPluginVersion = this.diConfigService.getDiConfig().WFMPCONTEXTLUGIN_VERSION;
            this.uvUpdater.setContextPluginVersion(wfmContextPluginVersion);
            this.uvUpdater.setLineshaftCallbackFunc(this.onResponseHandler);
            this.uvUpdater.setResponseCallback(this.uvUpdaterResponseCallBack);
            /* UVUpdater connection call */
            this.uvUpdater.reset();
            this.uvUpdater.connectUVUpdater();
            // If WebSocket connection is not established this cleanup method will execute otherwise the cleanup
            // method registered from loadPluginHandler will execute
            this.signOutService.registerCallback(this.lineshaftCleanUp);
          },
          reason => {
            log.error(
              'XMLTemplateManager initialize() - Error occurred while receiving the xml' +
                ' templates. Error Reason: ' +
                reason.statusText
            );
            this.notify.critical(
              this.translate.instant('di.desktopIntegration.warning.integrationDisabled'),
              this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
            );
          }
        );
        clearTimeout(initTimeout);
      }, 0);
    }
  }

  isCommonConfigInvalid() {
    log.info("DI Common Config - Server :'" + this.uvserver + "', Port :'" + this.uvPort + "'");
    return UtilService.isEmpty(this.uvserver) || UtilService.isEmpty(this.uvPort);
  }

  /**
   * Subscribes application terminate event from Lineshaft
   */
  private subscribeApplicationTerminateFromLineShaft() {
    const terminateResponse = this.xmlTemplateManagerService.getSubscribeXml(
      'TERMINATE_APPLICATION_RESPONSE',
      this.UVIEWER_CONFIG.PLUGIN_MANAGER
    );
    log.info('Subscribe the application terminated response from LineShaftPluginManager');
    this.diService.loadPluginsInvokeAPIWrapperExecute(terminateResponse);
  }

  private subscribePluginCloseResponse() {
    const subscribeXml = this.xmlTemplateManagerService.getSubscribeXml(
      this.UVIEWER_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_CLOSE_RESPONSE',
      this.UVIEWER_CONFIG.PLUGIN_MANAGER
    );
    log.info('Subscribe the setcontext response from ViewerPlugin');
    this.diService.loadPluginsInvokeAPIWrapperExecute(subscribeXml);
  }

  private displayNotificationMsgDetailsInSystemStatus(key, message) {
    this.appConfig.getSystemStatusNotifier().next({ key, message });
  }

  /**
   * registerCleanupCallback - Register a cleanup callback
   * @param callback - The cleanup callback function
   */
  registerCleanupCallback(callback: () => void) {
    const index = this.cleanupCallbacks.indexOf(callback);
    if (index !== -1) {
      this.cleanupCallbacks.splice(index, 1);
    }
    this.cleanupCallbacks.push(callback);
  }

  // lineshaftCleanUp plugins cleanup method
  private readonly lineshaftCleanUp = () => {
    log.info('CleanUp - for registered callbacks');
    while (this.cleanupCallbacks.length > 0) {
      const callback = this.cleanupCallbacks.pop();
      if (callback) {
        try {
          callback();
        } catch (error) {
          log.error('CleanUp - Error executing callback >> ', error);
        }
      }
    }

    log.info('CleanUp - for uvUpdater');
    if (this.uvUpdater) {
      // Deleting the UVUpdater object
      this.uvUpdater.exitUVUpdater();
      delete this.uvUpdater;
    }
  };
}
